import { CATEGORY_KEYS, INCOME_CAT_KEYS, CATEGORY_KEY_BY_LABEL, INCOME_CAT_KEY_BY_LABEL } from './constants';

// ─── Core CSV text parser ─────────────────────────────────────────────────────

export function parseCSVLine(line) {
  const fields = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') { field += '"'; i++; }
      else inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      fields.push(field.trim());
      field = '';
    } else {
      field += ch;
    }
  }
  fields.push(field.trim());
  return fields;
}

export function parseCSVText(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseCSVLine(lines[0]).map(h => h.replace(/^["']|["']$/g, '').trim());
  const rows = lines.slice(1)
    .filter(l => l.trim())
    .map(line => {
      const vals = parseCSVLine(line);
      const row = {};
      headers.forEach((h, i) => { row[h] = (vals[i] ?? '').trim(); });
      return row;
    });
  return { headers, rows };
}

// ─── Amount helpers ───────────────────────────────────────────────────────────

function parseAmount(str) {
  if (!str && str !== 0) return null;
  let s = String(str).replace(/[$,\s]/g, '');
  // Parenthesized negatives: (50.00) → -50.00
  if (s.startsWith('(') && s.endsWith(')')) s = '-' + s.slice(1, -1);
  // Trailing minus: 50.00- → -50.00
  if (s.endsWith('-')) s = '-' + s.slice(0, -1);
  const n = parseFloat(s);
  return isNaN(n) ? null : n;
}

// ─── Date parser — handles YYYY-MM-DD, M/D/YYYY, MM/DD/YYYY, M/D/YY ─────────

export function parseDate(str) {
  if (!str) return null;
  str = str.trim();
  // ISO
  if (/^\d{4}-\d{2}-\d{2}$/.test(str)) return str;
  // M/D/YYYY or MM/DD/YYYY
  const mdy4 = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdy4) {
    const [, mo, dy, yr] = mdy4;
    return `${yr}-${mo.padStart(2, '0')}-${dy.padStart(2, '0')}`;
  }
  // M/D/YY (2-digit year → 20YY)
  const mdy2 = str.match(/^(\d{1,2})\/(\d{1,2})\/(\d{2})$/);
  if (mdy2) {
    const [, mo, dy, yr] = mdy2;
    return `20${yr}-${mo.padStart(2, '0')}-${dy.padStart(2, '0')}`;
  }
  // D-Mon-YYYY  e.g. "02-Apr-2026"
  const dmy = str.match(/^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/);
  if (dmy) {
    const months = { jan:'01', feb:'02', mar:'03', apr:'04', may:'05', jun:'06',
                     jul:'07', aug:'08', sep:'09', oct:'10', nov:'11', dec:'12' };
    const mo = months[dmy[2].toLowerCase()];
    if (mo) return `${dmy[3]}-${mo}-${dmy[1].padStart(2, '0')}`;
  }
  return null;
}

// ─── Bank category → app category map ────────────────────────────────────────

const BANK_CAT_MAP = {
  // Food
  'restaurants':             'food_dining',
  'dining':                  'food_dining',
  'food':                    'food_dining',
  'fast food':               'food_dining',
  'coffee shops':            'food_dining',
  // Groceries
  'groceries':               'groceries',
  'supermarkets':            'groceries',
  // Gas
  'gas':                     'gas_fuel',
  'gas & fuel':              'gas_fuel',
  'fuel':                    'gas_fuel',
  // Transport
  'auto & transport':        'transportation',
  'transportation':          'transportation',
  'airlines':                'travel',
  'travel':                  'travel',
  // Lodging
  'hotel':                   'hotel_lodging',
  'hotels':                  'hotel_lodging',
  'lodging':                 'hotel_lodging',
  // Healthcare
  'healthcare':              'healthcare',
  'health & fitness':        'healthcare',
  'pharmacy':                'healthcare',
  'medical':                 'healthcare',
  // Shopping / Tech
  'shopping':                'shopping',
  'clothing':                'shopping',
  'electronics':             'technology',
  'technology':              'technology',
  'software':                'software',
  // Entertainment
  'entertainment':           'entertainment',
  'movies':                  'entertainment',
  'music':                   'entertainment',
  // Business
  'business services':       'office_business',
  'office supplies':         'office_business',
  // Other expense
  'education':               'education',
  'personal care':           'personal_care',
  'home':                    'home_garden',
  'home improvement':        'home_garden',
  'charity':                 'donations',
  'donations':               'donations',
  // Fees (expense)
  'fee':                     'bank_fees',
  'fees':                    'bank_fees',
  'bank fee':                'bank_fees',
  'commission':              'bank_fees',
  // Brokerage income — 4 distinct categories
  'interest':                'taxable_interest',
  'taxable interest':        'taxable_interest',
  'tax free interest':       'tax_exempt_interest',
  'tax exempt interest':     'tax_exempt_interest',
  'tax-free interest':       'tax_exempt_interest',
  'tax-exempt interest':     'tax_exempt_interest',
  'dividend':                'taxable_dividends',
  'dividends':               'taxable_dividends',
  'taxable dividend':        'taxable_dividends',
  'taxable dividends':       'taxable_dividends',
  'tax free dividend':       'tax_exempt_dividends',
  'tax exempt dividend':     'tax_exempt_dividends',
  'tax-free dividend':       'tax_exempt_dividends',
  'tax-exempt dividend':     'tax_exempt_dividends',
  'qualified dividend':      'taxable_dividends',
  'Tax Exempt Dividend':     'tax_exempt_dividends',
  // Combined catch-all
  'interest & dividends':    'interest_dividends',
  'interest and dividends':  'interest_dividends',
};

function mapCategory(raw) {
  if (!raw) return 'other';
  const lower = raw.toLowerCase().trim();
  if (BANK_CAT_MAP[lower]) return BANK_CAT_MAP[lower];
  for (const [k, v] of Object.entries(BANK_CAT_MAP)) {
    if (lower.includes(k) || k.includes(lower)) return v;
  }
  // Already a known key
  if (CATEGORY_KEYS[raw] || INCOME_CAT_KEYS[raw]) return raw;
  // Known label → convert to key
  const key = CATEGORY_KEY_BY_LABEL[raw] ?? INCOME_CAT_KEY_BY_LABEL[raw];
  if (key) return key;
  return 'other';
}

// ─── Credit card format definitions ──────────────────────────────────────────

const CREDIT_CARD_FORMATS = [
  {
    name: 'Visa / Corporate Card',
    detect: (h) => h.includes('Trans. Date') && h.includes('Transaction Type'),
    dateCol: 'Trans. Date',
    vendorCol: 'Description',
    amountCol: 'Amount',
    categoryCol: 'Expense Category',
    typeCol: 'Transaction Type',
    referenceCol: 'Reference ID',
    expenseIsNegative: false,
    resolveType: (typeVal, amount) => {
      if (typeVal === 'D') return 'charge';
      if (typeVal === 'C') return amount <= 0 ? 'payment' : 'refund';
      return 'charge';
    },
  },
  {
    name: 'Chase Credit Card',
    detect: (h) => h.includes('Transaction Date') && h.includes('Post Date') && h.includes('Type'),
    dateCol: 'Transaction Date',
    vendorCol: 'Description',
    amountCol: 'Amount',
    categoryCol: 'Category',
    typeCol: 'Type',
    referenceCol: null,
    expenseIsNegative: true,
    resolveType: (typeVal, amount) => {
      if (/payment/i.test(typeVal)) return 'payment';
      if (/return/i.test(typeVal)) return 'refund';
      if (/refund/i.test(typeVal)) return 'refund';
      if (amount > 0) return 'refund';
      return 'charge';
    },
  },
  // {
  //   name: 'American Express',
  //   detect: (h) => h.includes('Date') && h.includes('Card Member') && h.includes('Amount'),
  //   dateCol: 'Date',
  //   vendorCol: 'Description',
  //   amountCol: 'Amount',
  //   categoryCol: 'Category',
  //   typeCol: null,
  //   referenceCol: 'Reference',
  //   expenseIsNegative: false,
  //   resolveType: (_, amount) => amount < 0 ? 'payment' : 'charge',
  // },
  // {
  //   name: 'Capital One',
  //   detect: (h) => h.includes('Transaction Date') && h.includes('Debit') && h.includes('Credit') && h.includes('Card No.'),
  //   dateCol: 'Transaction Date',
  //   vendorCol: 'Description',
  //   amountCol: null,
  //   debitCol: 'Debit',
  //   creditCol: 'Credit',
  //   categoryCol: 'Category',
  //   typeCol: null,
  //   referenceCol: null,
  //   hasSeparateCols: true,
  //   expenseIsNegative: false,
  //   resolveType: (_, _amt, debit, credit) => credit ? 'payment' : 'charge',
  // },
  {
    name: 'Citibank',
    detect: (h) => h.includes('Status') && h.includes('Debit') && h.includes('Credit') && h.includes('Date'),
    dateCol: 'Date',
    vendorCol: 'Description',
    amountCol: null,
    debitCol: 'Debit',
    creditCol: 'Credit',
    categoryCol: null,
    typeCol: null,
    referenceCol: null,
    hasSeparateCols: true,
    expenseIsNegative: false,
    resolveType: (_, _amt, debit, credit) => credit ? 'payment' : 'charge',
  },

];

// ─── Brokerage format definitions ────────────────────────────────────────────

const BROKERAGE_FORMATS = [
  {
    name: 'ETrade Brokerage',
    detect: (h) => h.includes('Trade Date') && h.includes('Transaction Type') && h.includes('Security Description'),
    dateCol: 'Trade Date',
    vendorCol: 'Security Description',
    amountCol: 'Amount',
    categoryCol: 'Transaction Type',
    creditIsPositive: true,
  },
  {
    name: 'Schwab Brokerage',
    detect: (h) => h.includes('Action') && h.includes('Symbol') && h.includes('Description') && h.includes('Amount') && !h.includes('Posting Date'),
    dateCol: 'Date',
    vendorCol: 'Description',
    amountCol: 'Amount',
    categoryCol: 'Action',
    creditIsPositive: true,
  },
  {
    name: 'Fidelity Brokerage',
    detect: (h) => h.includes('Transaction') && h.includes('Security Description') && h.includes('Amount ($)'),
    dateCol: 'Date',
    vendorCol: 'Security Description',
    amountCol: 'Amount ($)',
    categoryCol: 'Transaction',
    creditIsPositive: true,
  },
  {
    name: 'TD Ameritrade',
    detect: (h) => h.includes('TRANSACTION TYPE') && h.includes('SECURITY') && h.includes('AMOUNT'),
    dateCol: 'DATE',
    vendorCol: 'DESCRIPTION',
    amountCol: 'AMOUNT',
    categoryCol: 'TRANSACTION TYPE',
    creditIsPositive: true,
  },
  {
    name: 'Vanguard Brokerage',
    detect: (h) => h.includes('Trade Date') && h.includes('Transaction Type') && h.includes('Investment Name'),
    dateCol: 'Trade Date',
    vendorCol: 'Investment Name',
    amountCol: 'Net Amount',
    categoryCol: 'Transaction Type',
    creditIsPositive: true,
  },
  {
    name: 'Generic Brokerage Activity',
    detect: (h) => h.includes('Activity Date') && h.includes('Activity') && h.includes('Amount($)'),
    dateCol: 'Activity Date',
    vendorCol: 'Description',
    amountCol: 'Amount($)',
    categoryCol: 'Activity',
    creditIsPositive: true,
  },
];

// ─── Bank statement format definitions ───────────────────────────────────────

const BANK_STATEMENT_FORMATS = [
  {
    name: 'Chase Bank',
    detect: (h) => h.includes('Details') && h.includes('Posting Date') && h.includes('Balance'),
    dateCol: 'Posting Date',
    vendorCol: 'Description',
    amountCol: 'Amount',
    typeCol: 'Type',
    referenceCol: 'Check or Slip #',
    creditIsPositive: true, // positive = deposit/income
  },
  {
    name: 'Bank of America',
    detect: (h) => h.includes('Date') && h.includes('Running Bal.'),
    dateCol: 'Date',
    vendorCol: 'Description',
    amountCol: 'Amount',
    typeCol: null,
    referenceCol: null,
    creditIsPositive: true,
  },
  // {
  //   name: 'Wells Fargo Bank',
  //   detect: (h) => h.length >= 5 && h[0] === '' && h[1] === '' && h[2] === '',
  //   dateCol: '__col0',
  //   vendorCol: '__col4',
  //   amountCol: '__col1',
  //   typeCol: null,
  //   referenceCol: '__col5',
  //   creditIsPositive: true,
  //   indexBased: true,
  // },
  {
    name: 'Etrade Bank',
    detect: (h) => h.includes('Activity Type') && h.includes('Amount $') && h.includes('Transaction Date'),
    dateCol: 'Transaction Date',
    vendorCol: 'Description',
    amountCol: 'Amount $',
    categoryCol: 'Activity Type',
    creditIsPositive: true,
  },

  {
    name: 'Etrade Bank2',
    detect: (h) => h.includes('TransactionType') && h.includes('Amount') && h.includes('TransactionDate'),
    dateCol: 'TransactionDate',
    vendorCol: 'Description',
    amountCol: 'Amount',
    categoryCol: 'TransactionType',
    creditIsPositive: true,
  },
];

// ─── Format detection ─────────────────────────────────────────────────────────

export function detectCreditCardFormat(headers) {
  for (const fmt of CREDIT_CARD_FORMATS) {
    if (fmt.detect(headers)) return fmt;
  }
  return null;
}

export function detectBankFormat(headers) {
  for (const fmt of BANK_STATEMENT_FORMATS) {
    if (fmt.detect(headers)) return fmt;
  }
  return null;
}

export function detectBrokerageFormat(headers) {
  for (const fmt of BROKERAGE_FORMATS) {
    if (fmt.detect(headers)) return fmt;
  }
  return null;
}

// Try CC first, then bank — useful for ambiguous files
export function detectAnyFormat(headers) {
  const cc = detectCreditCardFormat(headers);
  if (cc) return { format: cc, type: 'credit_card' };
  const brokerage = detectBrokerageFormat(headers);
  if (brokerage) return { format: brokerage, type: 'brokerage' };
  const bank = detectBankFormat(headers);
  if (bank) return { format: bank, type: 'bank' };
  return null;
}

// ─── Row normalizers ──────────────────────────────────────────────────────────

function getCol(row, col, headers) {
  if (!col) return '';
  if (col.startsWith('__col')) {
    return row[headers[parseInt(col.replace('__col', ''))]] ?? '';
  }
  return row[col] ?? '';
}

export function normalizeCreditCardRow(row, fmt, headers) {
  const dateStr = getCol(row, fmt.dateCol, headers);
  const date = parseDate(dateStr);
  if (!date) return null;

  const vendor = getCol(row, fmt.vendorCol, headers).trim();
  if (!vendor) return null;

  let rawAmount, transactionType;

  if (fmt.hasSeparateCols) {
    const debit = parseAmount(getCol(row, fmt.debitCol, headers));
    const credit = parseAmount(getCol(row, fmt.creditCol, headers));
    rawAmount = debit || credit || 0;
    transactionType = fmt.resolveType(null, rawAmount, debit, credit);
  } else {
    rawAmount = parseAmount(getCol(row, fmt.amountCol, headers));
    if (rawAmount === null) return null;
    const typeVal = fmt.typeCol ? getCol(row, fmt.typeCol, headers) : '';
    transactionType = fmt.resolveType(typeVal, rawAmount);
  }

  const amount = Math.abs(rawAmount);
  const category = mapCategory(getCol(row, fmt.categoryCol, headers));
  const referenceId = fmt.referenceCol ? getCol(row, fmt.referenceCol, headers) : null;

  return {
    date,
    vendor,
    amount,
    category,
    transactionType,   // 'charge' | 'payment' | 'refund'
    referenceId: referenceId || null,
    currency: 'USD',
    sourceFormat: fmt.name,
  };
}

export function normalizeBankRow(row, fmt, headers) {
  const dateStr = getCol(row, fmt.dateCol, headers);
  const date = parseDate(dateStr);
  if (!date) return null;

  const vendor = getCol(row, fmt.vendorCol, headers).trim();
  if (!vendor) return null;

  const rawAmount = parseAmount(getCol(row, fmt.amountCol, headers));
  if (rawAmount === null) return null;

  // For bank statements: positive = credit (money in), negative = debit (money out)
  const isCredit = fmt.creditIsPositive ? rawAmount > 0 : rawAmount < 0;
  const amount = Math.abs(rawAmount);

  const referenceId = fmt.referenceCol ? getCol(row, fmt.referenceCol, headers) : null;

  return {
    date,
    vendor,
    amount,
    category: fmt.categoryCol ? mapCategory(getCol(row, fmt.categoryCol, headers)) : 'other',
    transactionType: isCredit ? 'credit' : 'debit',   // bank direction
    referenceId: referenceId || null,
    currency: 'USD',
    sourceFormat: fmt.name,
  };
}

// ─── Duplicate detection ──────────────────────────────────────────────────────

// Returns true if |dateA - dateB| <= maxDays
function datesClose(a, b, maxDays) {
  if (!a || !b) return false;
  const diff = Math.abs(new Date(a) - new Date(b));
  return diff <= maxDays * 86400000;
}

// Resolve credit/debit direction from whichever field is available
function getDirection(tx) {
  if (tx.transactionDirection) return tx.transactionDirection;
  // Bank rows use transactionType directly as 'credit'/'debit'
  if (tx.transactionType === 'credit') return 'credit';
  if (tx.transactionType === 'debit') return 'debit';
  // CC rows: payment/refund are credits; charges are debits
  if (tx.transactionType === 'payment' || tx.transactionType === 'refund') return 'credit';
  return 'debit';
}

// Fuzzy-match a detected format name against a list of account objects { name }.
// Returns the best matching account name, or null if nothing scores above 0.
export function fuzzyMatchAccount(formatName, accounts) {
  if (!accounts.length) return null;
  const normalize = s => s.toLowerCase()
    .replace(/\bamex\b/g, 'american express')
    .replace(/\bbofa\b|\bboa\b/g, 'bank of america')
    .replace(/\bciti\b/g, 'citibank');
  const fTokens = normalize(formatName).split(/\W+/).filter(t => t.length > 2);
  let best = null, bestScore = 0;
  for (const a of accounts) {
    const aTokens = normalize(a.name).split(/\W+/).filter(t => t.length > 2);
    let score = 0;
    for (const ft of fTokens) {
      for (const at of aTokens) {
        if (at === ft) score += 2;
        else if (at.startsWith(ft) || ft.startsWith(at)) score += 1;
      }
    }
    if (score > bestScore) { bestScore = score; best = a.name; }
  }
  return bestScore > 0 ? best : null;
}

// Score a CSV filename against every account, returning [{name, score}] sorted descending.
// Strips file extensions and date suffixes before tokenising so "chase_2024-03.csv"
// matches "Chase Checking" on the "chase" token, not on the date.
export function scoreFilenameAgainstAccounts(filename, accounts) {
  if (!accounts.length) return [];
  const normalize = s => s.toLowerCase()
    .replace(/\.[^.]+$/, '')
    .replace(/\d{4}[-_]\d{2}([-_]\d{2})?/g, '')
    .replace(/\b(jan|feb|mar|apr|may|jun|jul|aug|sep|oct|nov|dec)\w*[\s_-]*\d{2,4}/gi, '')
    .replace(/\bamex\b/g, 'american express')
    .replace(/\bbofa\b|\bboa\b/g, 'bank of america')
    .replace(/\bciti\b/g, 'citibank');
  const fTokens = normalize(filename).split(/\W+/).filter(t => t.length > 2);
  return accounts
    .map(a => {
      const aTokens = normalize(a.name).split(/\W+/).filter(t => t.length > 2);
      let score = 0;
      for (const ft of fTokens) {
        for (const at of aTokens) {
          if (at === ft) score += 2;
          else if (at.startsWith(ft) || ft.startsWith(at)) score += 1;
        }
      }
      return { name: a.name, score };
    })
    .sort((a, b) => b.score - a.score);
}

export function findDuplicate(tx, existingList) {
  const txAmount = parseFloat(tx.amount ?? tx.total ?? 0);
  const txDir = getDirection(tx);

  for (const ex of existingList) {
    const exDir = getDirection(ex);
    // Direction must match — a credit can never be a duplicate of a debit
    if (txDir !== exDir) continue;

    // Exact reference ID match (strongest signal)
    if (tx.referenceId && ex.referenceId && tx.referenceId === ex.referenceId) {
      return ex;
    }

    // Exact amount + date window + same vendor
    const exAmount = parseFloat(ex.total ?? ex.amount ?? 0);
    const amountExact = Math.abs(exAmount - txAmount) < 0.001;
    const vendorClose = (ex.vendor || '').toLowerCase() === (tx.vendor || '').toLowerCase();
    if (amountExact && datesClose(tx.date, ex.date, 2) && vendorClose) {
      return ex;
    }
  }
  return null;
}

// Detect transfer pairs within a mixed list (credit card payment ↔ bank debit)
// Returns array of { a, b } pairs
export function detectTransferPairs(ccTransactions, bankTransactions) {
  const pairs = [];
  const usedBank = new Set();

  for (const cc of ccTransactions) {
    if (cc.transactionType !== 'payment') continue;
    for (let i = 0; i < bankTransactions.length; i++) {
      const bank = bankTransactions[i];
      if (usedBank.has(i)) continue;
      if (bank.transactionType !== 'debit') continue;
      const amountClose = Math.abs(cc.amount - bank.amount) <= 0.02;
      if (amountClose && datesClose(cc.date, bank.date, 3)) {
        pairs.push({ cc, bank });
        usedBank.add(i);
        break;
      }
    }
  }
  return pairs;
}

// Detect bank-to-bank transfer pairs: debit on one account ↔ credit on a different account.
// Transactions must carry a _reportName field so same-account matches are excluded.
// Returns array of { debit, credit } pairs.
export function detectBankTransferPairs(bankTransactions) {
  const pairs = [];
  const usedCredits = new Set();
  const debits  = bankTransactions.filter(tx => tx.transactionType === 'debit');
  const credits = bankTransactions.filter(tx => tx.transactionType === 'credit');

  for (const debit of debits) {
    for (let i = 0; i < credits.length; i++) {
      if (usedCredits.has(i)) continue;
      const credit = credits[i];
      if (debit._reportName && credit._reportName && debit._reportName === credit._reportName) continue;
      const dAmt = parseFloat(debit.amount ?? debit.total ?? 0);
      const cAmt = parseFloat(credit.amount ?? credit.total ?? 0);
      if (Math.abs(dAmt - cAmt) <= 0.02 && datesClose(debit.date, credit.date, 3)) {
        pairs.push({ debit, credit });
        usedCredits.add(i);
        break;
      }
    }
  }
  return pairs;
}

// ─── Header row scanner ───────────────────────────────────────────────────────

// Scans up to maxSearch lines looking for the first row that detectFn recognises
// as a known format header. Returns { headerIdx, headers, fmt } or null.
function findHeaderRow(lines, detectFn, maxSearch = 15) {
  const limit = Math.min(lines.length, maxSearch);
  for (let i = 0; i < limit; i++) {
    const headers = parseCSVLine(lines[i]).map(h => h.replace(/^["']|["']$/g, '').trim());
    const fmt = detectFn(headers);
    if (fmt) return { headerIdx: i, headers, fmt };
  }
  return null;
}

// Build row objects from lines after the header row
function buildRows(lines, headerIdx, headers) {
  return lines.slice(headerIdx + 1)
    .filter(l => l.trim())
    .map(line => {
      const vals = parseCSVLine(line);
      const row = {};
      headers.forEach((h, i) => { row[h] = (vals[i] ?? '').trim(); });
      return row;
    });
}

// ─── Main parse entry points ──────────────────────────────────────────────────

// Mark isDuplicate/duplicateOf on newTxs against a list of existingTxs.
// Returns a new array (does not mutate).
export function markDuplicates(newTxs, existingTxs = []) {
  return newTxs.map(tx => {
    const dup = findDuplicate(tx, existingTxs);
    return { ...tx, isDuplicate: !!dup, duplicateOf: dup?.id ?? null };
  });
}

// ─── AI-detected format fallback ─────────────────────────────────────────────

// Normalize one row using AI-detected column mapping.
// aiCols: { headerRowIdx, dateCol, vendorCol, amountCol, debitCol, creditCol,
//           creditIsPositive, transactionTypeCol, paymentIndicators, categoryCol }
// Returns { date, vendor, amount, category, isCredit, isPayment } or null.
function normalizeAIRow(row, aiCols, headers) {
  const col = (idx) => getCol(row, `__col${idx}`, headers);

  const date = parseDate(col(aiCols.dateCol));
  if (!date) return null;

  const vendor = col(aiCols.vendorCol).trim();
  if (!vendor) return null;

  let amount, isCredit;

  if (aiCols.debitCol !== null && aiCols.creditCol !== null) {
    // Split debit/credit columns (Capital One, Citibank style)
    const debit  = parseAmount(col(aiCols.debitCol));
    const credit = parseAmount(col(aiCols.creditCol));
    if (credit !== null && credit !== 0) {
      amount = Math.abs(credit);
      isCredit = true;
    } else if (debit !== null && debit !== 0) {
      amount = Math.abs(debit);
      isCredit = false;
    } else {
      return null;
    }
  } else if (aiCols.amountCol !== null) {
    const raw = parseAmount(col(aiCols.amountCol));
    if (raw === null) return null;
    isCredit = (aiCols.creditIsPositive ?? true) ? raw > 0 : raw < 0;
    amount = Math.abs(raw);
  } else {
    return null;
  }

  // Merchant category
  let category = 'Other';
  if (aiCols.categoryCol !== null) {
    const rawCat = col(aiCols.categoryCol);
    if (rawCat) category = mapCategory(rawCat);
  }

  // Payment/transfer detection from type column
  let isPayment = false;
  if (aiCols.transactionTypeCol !== null && (aiCols.paymentIndicators || []).length > 0) {
    const typeVal = col(aiCols.transactionTypeCol).trim().toLowerCase();
    const indicators = aiCols.paymentIndicators.map(s => s.toLowerCase());
    isPayment = indicators.some(p => typeVal === p || typeVal.includes(p));
  }

  return { date, vendor, amount, category, isCredit, isPayment };
}

// aiCols: { headerRowIdx, dateCol, vendorCol, amountCol, debitCol, creditCol,
//           creditIsPositive, transactionTypeCol, paymentIndicators, categoryCol }
// type: 'credit_card' | 'bank'
export function parseCSVWithAIFormat(text, aiCols, type) {
  const hasAmount = aiCols.amountCol !== null ||
    (aiCols.debitCol !== null && aiCols.creditCol !== null);
  if (aiCols.dateCol === null || aiCols.vendorCol === null || !hasAmount) {
    return { format: null, transactions: [], skipped: 0 };
  }
  console.log('AI Cols', aiCols);
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const headerIdx = aiCols.headerRowIdx ?? 0;
  if (lines.length <= headerIdx) return { format: null, transactions: [], skipped: 0 };

  const headers = parseCSVLine(lines[headerIdx]).map(h => h.replace(/^["']|["']$/g, '').trim());
  const rows = buildRows(lines, headerIdx, headers);
  const transactions = [];
  let skipped = 0;

  for (const row of rows) {
    const norm = normalizeAIRow(row, aiCols, headers);
    if (!norm) { skipped++; continue; }
    // console.log('Norm', norm);
    const { date, vendor, amount, category, isCredit, isPayment } = norm;
    const id = `import-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    const base = { id, date, vendor, amount, category, total: amount, currency: 'USD',
                   sourceFormat: 'AI Detected', isDuplicate: false, duplicateOf: null };

    if (type === 'credit_card') {
      // isPayment (from type column) takes priority; otherwise fall back to credit direction
      const txType = isPayment ? 'payment'
                   : isCredit  ? 'refund'
                                : 'charge';
      transactions.push({
        ...base,
        transactionType:      txType,
        transactionDirection: txType === 'charge' ? 'debit' : 'credit',
        ...(txType === 'payment' ? { businessClassification: 'transfer' } : {}),
        ...(txType === 'refund'  ? { businessClassification: 'unclassified_credit' } : {}),
      });
    } else {
      // Bank / brokerage: payments/transfers detected by type column → transfer
      transactions.push({
        ...base,
        transactionType:      isCredit ? 'credit' : 'debit',
        transactionDirection: isCredit ? 'credit' : 'debit',
        businessClassification: isPayment ? 'transfer'
                               : isCredit  ? 'unclassified_credit'
                                           : 'unclassified_debit',
      });
    }
  }

  return { format: 'AI Detected', transactions, skipped, aiDetected: true };
}

export function parseCreditCardCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const found = findHeaderRow(lines, detectCreditCardFormat);
  if (!found) return { format: null, transactions: [], skipped: 0 };

  const { headerIdx, headers, fmt } = found;
  const rows = buildRows(lines, headerIdx, headers);
  const transactions = [];
  let skipped = 0;

  for (const row of rows) {
    const tx = normalizeCreditCardRow(row, fmt, headers);
    if (!tx) { skipped++; continue; }
    transactions.push({
      ...tx,
      id: `import-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isDuplicate: false,
      duplicateOf: null,
      total: tx.amount,
      transactionDirection: ['payment', 'refund'].includes(tx.transactionType) ? 'credit' : 'debit',
      ...(tx.transactionType === 'payment' ? { businessClassification: 'transfer' } : {}),
      ...(tx.transactionType === 'refund'  ? { businessClassification: 'unclassified_credit' } : {}),
    });
  }

  return { format: fmt.name, transactions, skipped };
}

export function parseBankStatementCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const found = findHeaderRow(lines, detectBankFormat);
  if (!found) return { format: null, transactions: [], skipped: 0 };

  const { headerIdx, headers, fmt } = found;
  const rows = buildRows(lines, headerIdx, headers);
  const transactions = [];
  let skipped = 0;

  for (const row of rows) {
    const tx = normalizeBankRow(row, fmt, headers);
    if (!tx) { skipped++; continue; }
    transactions.push({
      ...tx,
      id: `import-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isDuplicate: false,
      duplicateOf: null,
      total: tx.amount,
      transactionDirection: tx.transactionType, // 'credit' or 'debit'
      businessClassification: tx.transactionType === 'credit' ? 'unclassified_credit' : 'unclassified_debit',
    });
  }

  return { format: fmt.name, transactions, skipped };
}

export function parseBrokerageCSV(text) {
  const lines = text.split(/\r?\n/).filter(l => l.trim());
  const found = findHeaderRow(lines, detectBrokerageFormat);
  if (!found) return { format: null, transactions: [], skipped: 0 };

  const { headerIdx, headers, fmt } = found;
  const rows = buildRows(lines, headerIdx, headers);
  const transactions = [];
  let skipped = 0;

  for (const row of rows) {
    const tx = normalizeBankRow(row, fmt, headers); // same credit/debit logic
    if (!tx) { skipped++; continue; }
    transactions.push({
      ...tx,
      id: `import-${Date.now()}-${Math.random().toString(36).slice(2)}`,
      isDuplicate: false,
      duplicateOf: null,
      total: tx.amount,
      transactionDirection: tx.transactionType,
      businessClassification: tx.transactionType === 'credit' ? 'unclassified_credit' : 'unclassified_debit',
    });
  }

  return { format: fmt.name, transactions, skipped };
}
